import { getAuth, signInAnonymously } from 'firebase/auth'
import { isProduction } from '../Utils'

const API_URL = '/api'

const TICKET_STATUS_IN_PROGRESS = 1
const TICKET_STATUS_MATCHED = 2
const TICKET_STATUS_COMPLETE = 3

export default class FirebaseClient {
  constructor(clientVersion) {
    this._firebaseUser = null
    this._firebaseIdToken = null
    this._apiUser = null
    this._authenticated = false
    this._clientVersion = clientVersion
  }

  get idToken() {
    return this._firebaseIdToken
  }

  get authenticated() {
    return this._authenticated
  }

  get user() {
    return this._firebaseUser
  }

  async signIn(user) {
    if (user && !user.isAnonymous) {
      console.log('login authenticated', user)

      this._firebaseUser = user
      this._firebaseIdToken = await user.getIdToken(true)

      this._apiUser = { user_id: this._firebaseUser.uid }

      this._anonymous = false
    } else if (!this._apiUser) {
      console.log('login anonymous', user)

      const auth = getAuth()

      const userCredential = await signInAnonymously(auth)
      this._firebaseUser = userCredential.user

      console.log('firebase user', this._firebaseUser)

      this._firebaseIdToken = await auth.currentUser.getIdToken(true)

      this._apiUser = await this._updateOrCreateUser({
        countryCode: ((navigator.language || navigator.languages[0]).match(/[A-Z]{2}/) || ['PL'])[0],
        avatarId: Math.round(Math.random()) + 1
      })

      this._anonymous = true
    }

    this._authenticated = true
  }

  isAnonymous() {
    return this._anonymous
  }

  async getGroups(locale = 'EN') {
    const result = await this._request(
      'GET',
      '/rooms/groups?filter[locale]=' +
        locale +
        '&filter[show_on_room_list]=true&filter[modifier_ids][]=100&filter[modifier_ids][]=2'
    )

    return result.data
  }

  async getRooms(roomId = null) {
    return (await this.getGroups())
      .filter(
        (group) =>
          roomId === null || group.rooms.find((room) => room.id === roomId)
      )
      .map((group) => {
        return {
          group: { id: group.id, name: group.name },
          rooms: group.rooms.filter(
            (room) => room.enabled && (roomId === null || room.id === roomId)
          ),
        }
      })
      .filter((group) => group.rooms.length > 0)
  }

  async makeMatch(roomId, tournamentId) {
    // eslint-disable-next-line
    return new Promise(async (resolve, reject) => {
      const ticket = await this._createMatchMakingTicket(roomId, tournamentId)

      const checkStatus = async () => {
        let ticketData

        try {
          ticketData = await this._getTicketData(ticket.ticket_id)
        } catch (e) {
          reject(e)
          return
        }

        if (ticketData.status === TICKET_STATUS_IN_PROGRESS) {
          this._ticketCheckStatus = setTimeout(checkStatus, 1000)
        } else if (ticketData.status === TICKET_STATUS_MATCHED) {
          this._ticketCheckStatus = setTimeout(checkStatus, 500)
        } else if (ticketData.status === TICKET_STATUS_COMPLETE) {
          if (ticketData.features.rts) {
            resolve(ticketData)
          } else {
            reject()
          }
        }
      }

      checkStatus()
    })
  }

  disconnect() {
    clearTimeout(this._ticketCheckStatus)
  }

  async _getTicketData(ticketId) {
    const result = await this._request(
      'GET',
      '/matchmaking/tickets/' + ticketId
    )

    return result.data
  }

  async _createMatchMakingTicket(roomId, tournamentId) {
    const url = "/matchmaking/tickets";
    // to do
    const queryParams = new URLSearchParams();
    const devFlags = isProduction() ? null : ['mm_simple_match'];
    if (devFlags) {
      devFlags.forEach(flag => {
        queryParams.append('dev_flags[]', flag);
      });
    }
    const requestUrl = `${url}?${queryParams.toString()}`;

    const body = {
      client_capabilities: {
        // turn_clock_sync: true,
        rts: true,
      }
    }

    if (tournamentId) {
      body.tournament_id = tournamentId;
    } else {
      body.room_id = roomId;
    }

    const result = await this._request('POST', requestUrl, body);

    return result.data
  }

  async _updateOrCreateUser(playerData) {
    const data = {
      name:
        playerData && playerData.name
          ? playerData.name
          : 'player' + Math.round(Math.random() * Date.now()),
      avatar_id: playerData && playerData.avatarId ? playerData.avatarId : 1,
      country_code:
        playerData && playerData.countryCode ? playerData.countryCode : 'PL',
      vip: false,
    }

    let result

    try {
      await this._request('PUT', '/users/' + this._firebaseUser.uid, data)
      result = { data: { user_id: this._firebaseUser.uid } }
    } catch (e) {
      result = await this._request(
        'POST',
        '/users/' + this._firebaseUser.uid,
        data
      )
    }

    return result.data
  }

  async _request(method, url, data) {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest()

      url = API_URL + url

      req.onreadystatechange = () => {
        if (req.readyState === 4) {
          const isJSON = (
            req.getResponseHeader('content-type') || ''
          ).startsWith('application/json')

          if (req.status === 200) {
            if (isJSON) {
              resolve(JSON.parse(req.responseText))
            } else {
              resolve(req.responseText)
            }
          } else {
            if (isJSON) {
              reject(JSON.parse(req.responseText))
            } else {
              reject(req.responseText)
            }
          }
        }
      }

      req.open(method, url, true)
      req.setRequestHeader('authorization', 'Bearer ' + this._firebaseIdToken)
      req.setRequestHeader(
        'client-version',
        `${this._clientVersion.APP_NAME}/${this._clientVersion.APP_VERSION} (${this._clientVersion.APP_PLATFORM})`
      )

      if (data && (data instanceof Array || data instanceof Object)) {
        req.setRequestHeader('content-type', 'application/json')
      }

      req.send(data ? JSON.stringify(data) : undefined)
    })
  }
}
